import { fetch as customFetch } from '@utils/utils';
// import * as nativeFetch from 'node-fetch';
import * as T from '@modules/events/_eventsTypes';
import { FileRef } from '@utils/types';
import { get } from 'lodash';
import * as uuid from 'uuid/v4';
import { Filter } from 'src/types';

export const createEventRecord = async ({ fileId, eventId, ...formData }) => {
  try {
    const response: any = await customFetch('/metropolis/api/ed/records/create', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { fileId, eventId },
      body: JSON.stringify(formData)
    });

    return response.ok;
  } catch (err) {
    return false;
  }
};

export const normalizeCrzWeaponsLookupResult = result => {
  const weapons = Array.isArray(result)
    ? result
    : get(
        result,
        'weapons',
        get(
          result,
          'zbrane',
          get(result, 'weaponList', get(result, 'zbrojniList', get(result, 'items', get(result, 'data', []))))
        )
      );

  const normalizedWeapons = Array.isArray(weapons) ? weapons : weapons ? [weapons] : [];

  return {
    licenseType: get(result, 'licenseType'),
    licenseState: get(result, 'licenseState'),
    weapons: normalizedWeapons.map(weapon => ({
      ...weapon,
      brand: get(weapon, 'brand', get(weapon, 'manufacturer')),
      manufacturer: get(weapon, 'manufacturer', get(weapon, 'brand')),
      model: get(weapon, 'model', get(weapon, 'pattern')),
      pattern: get(weapon, 'pattern', get(weapon, 'model')),
      caliber: get(weapon, 'caliber', get(weapon, 'calibers', []).join(', ')),
      licenseType: get(weapon, 'licenseType', get(result, 'licenseType')),
      licenseState: get(weapon, 'licenseState', get(result, 'licenseState'))
    }))
  };
};

export const crzLookupWeapons = async (person: any): Promise<any> => {
  try {
    const response: any = await customFetch('/metropolis/api/common/crz/zbrojni-list', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(person)
    });

    if (response.ok) {
      const json = await response.json();
      return normalizeCrzWeaponsLookupResult(json);
    }

    return { weapons: [] };
  } catch (err) {
    return { weapons: [] };
  }
};
export const syncEventRecord = async ({ eventId, ...formData }): Promise<boolean> => {
  try {
    const response: any = await customFetch('/metropolis/api/ed/records/sync', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { eventId },
      body: JSON.stringify(formData)
    });

    return response.ok;
  } catch (err) {
    return false;
  }
};
export const duplicateEvent = async (eventId: string): Promise<string | null> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/events/${eventId}/duplicate`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    return await response.text();
  } catch (err) {
    return null;
  }
};

// filter entities with deleted flag
const filterDeleted = (eventObject, deletedPaths: string[]) => {
  deletedPaths.forEach(deletedPath => {
    eventObject = {
      ...eventObject,
      [deletedPath]: get(eventObject, deletedPath).filter(d => !d.deleted)
    };
  });
  return eventObject;
};

const getLockedFullName = async ({ userId, addError }) => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/events/lock/user?userId=${userId}`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    const userName = await response.text();
    return userName;
  } catch (err) {
    addError(err.message);
    return '';
  }
};

export const getEvent = ({ addError }) => handleLockedUser => async (id: string): Promise<T.Event> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/events/${id}`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    const userId = response.headers.get('event-lock-user-id');
    if (userId) {
      const fullName = userId ? await getLockedFullName({ userId, addError }) : '';
      handleLockedUser({ id: userId, fullName });
    }

    const json = await response.json();
    const eventFilterDeletedData = filterDeleted(json, ['delicts', 'participants']);
    return eventFilterDeletedData;
  } catch (err) {
    addError(err.message);
    return {};
  }
};

export const putEvent = ({ addError, addSuccess }) => async (body: T.Event): Promise<T.Event | null> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/events/v2/${body.id}`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });

    if (response.ok) {
      addSuccess('Událost byla úspěšne uložena');
      const json: T.Event = await response.json();
      return json;
    } else {
      addError('Událost nebyla uložena');
      return null;
    }
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getRecordId = async (eventId: string): Promise<string | null> => {
  try {
    const response: any = await customFetch(
      `/metropolis/api/common/sps/records?pageSize=999&page=0&filter%5B0%5D.field=internalId&filter%5B0%5D.operation=EQ&filter%5B0%5D.value=${eventId}&allowDeleted=false`,
      {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/json' })
      }
    );

    if (response.ok) {
      const json = await response.json();
      return json.count ? json.items[0].internalId : null;
    } else {
      return null;
    }
  } catch (err) {
    return null;
  }
};

export const processPhotos = async (body: any): Promise<T.Event | null> => {
  try {
    const response: any = await customFetch('/metropolis/api/ed/prolaser/process-photos', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });

    if (response.ok) {
      const json: T.Event = await response.json();
      return json;
    } else {
      return null;
    }
  } catch (err) {
    return null;
  }
};

export const generatePdf = async (
  eventId: string
): Promise<{
  id?: string;
}> => {
  try {
    const response: any = await customFetch('/metropolis/api/ed/events/description/export-pdf', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { eventId }
    });

    if (response.ok) {
      const json = await response.json();

      return json;
    } else {
      return {};
    }
  } catch (err) {
    return {};
  }
};

export const getDescriptionTemplate = async (eventId): Promise<any> => {
  try {
    const response: any = await customFetch('/metropolis/api/ed/events/description/generate', {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { eventId }
    });

    if (response.ok) {
      const text = await response.text();
      return text.replace(/[\r\n]+/g, '\n').replace(/(?<=":"[^"]*)\n(?=[^"]*"[,}])/g, '\\n');
    } else {
      return null;
    }
  } catch (err) {
    return null;
  }
};

export const saveDescriptionAsFile = async (eventId: string, name: string, content: string): Promise<boolean> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/event/files/create-description`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { eventId, name },
      body: content
    });

    return response.ok;
  } catch (e) {
    return false;
  }
};

export const getDraftContent = async (draftId: string): Promise<string | null> => {
  const response: any = await customFetch(`/metropolis/api/files/${draftId}`, {
    method: 'GET',
    headers: new Headers({ 'Content-Type': 'application/json' })
  });
  if (response.ok) {
    const result = await response.text();
    return result;
  }
  return null;
};

export const updateDraftContent = async (eventId: string, fileId: string, content: string): Promise<boolean> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/event/files/${fileId}/update-description`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { eventId },
      body: content
    });

    return response.ok;
  } catch (e) {
    return false;
  }
};

export const getSyncedFiles = async eventId => {
  try {
    const response = await customFetch(
      `/metropolis/api/common/sps/documents?filter[0].field=internalId&filter[0].operation=EQ&filter[0].value=${eventId}`,
      {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/json' })
      }
    );
    const syncedFiles = await response.json();
    return syncedFiles;
  } catch (e) {
    return { count: 0, items: [] };
  }
};

export const lockEvent = ({ addSuccess, addError }) => async eventId => {
  try {
    const response = await customFetch(`/metropolis/api/ed/events/${eventId}/lock`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    if (response.ok) {
      addSuccess('Událost úspěšně uzamknuta');
    }
    return response.ok;
  } catch (e) {
    addError('Událost sa nepodařilo uzamknout');
    return false;
  }
};

export const unlockEvent = ({ addSuccess, addError }) => async eventId => {
  try {
    const response = await customFetch(`/metropolis/api/ed/events/${eventId}/unlock`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    if (response.ok) {
      addSuccess('Událost úspěšně odemknuta');
    }
    return response.ok;
  } catch (e) {
    addError('Událost sa nepodařilo odemknout');
    return false;
  }
};

export const getIsepFile = ({ addError }) => async (
  delictId: string,
  participantId: string
): Promise<string | null> => {
  try {
    const response = await customFetch(`/metropolis/api/ed/isep/check-delicts`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { delictId, participantId }
    });
    const isep = await response.json();
    const isepId = get(isep, 'id');

    return isepId;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getIsrtFile = ({ addError }) => async (
  delictId: string,
  participantId: string
): Promise<{ files: FileRef[]; state: string } | null> => {
  try {
    const response = await customFetch(`/metropolis/api/ed/isrt/check-records`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { delictId, participantId }
    });

    return await response.json();
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getEventDefaultPlace = async (): Promise<object | null> => {
  try {
    const response: any = await customFetch('/metropolis/api/settings/general', {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    const settingsObject = await response.json();
    const eventDefaultPlace = get(settingsObject, `otherSettings.eventDefaultPlace`, null);

    return eventDefaultPlace;
  } catch (err) {
    return null;
  }
};

export const getAudioRecords = ({ addError }) => async (pbx, formData) => {
  try {
    const response = await customFetch(`/metropolis/api/ed/${pbx}/records`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: formData
    });

    const result = await response.json();
    return result;
  } catch (err) {
    addError(err.message);
  }
  return { data: [], pageNext: -1 };
};

export const saveAudioRecord = ({ addError }) => async (pbx, eventId, recordId) => {
  try {
    const response = await customFetch(`/metropolis/api/ed/${pbx}/${eventId}/process-records`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { recordId }
    });
    return response;
  } catch (err) {
    addError(err.message);
  }
};

export const addReportToEvent = ({ addError }) => async (eventId, reportId, input) => {
  try {
    const response = await customFetch(`/metropolis/api/reports/${reportId}/generate-to-event`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(input),
      params: { eventId, index: false }
    });

    if (response.ok) {
      return true;
    } else {
      const error = await response.text();
      return addError(error);
    }
  } catch (err) {
    return addError(err.message);
  }
};

export const putParticipant = ({ addError, addSuccess }) => async (formData: any, id?: string): Promise<any> => {
  try {
    const isEditing = id;
    id = id ? id : uuid();
    const { photoTmp, eventParticipant } = formData;
    const response = await customFetch(`/metropolis/api/ed/event/participants/${id}`, {
      method: 'PUT',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ photoTmp, eventParticipant: { ...eventParticipant, id } })
    });
    if (response.ok) {
      addSuccess(isEditing ? 'Účastník byl změněn' : 'Účastník byl přidán');
      return await response.json();
    } else {
      const error = await response.text();
      addError(error);
      return {};
    }
  } catch (err) {
    addError(err.message);
    return {};
  }
};

export const deleteParticipant = ({ addError, addSuccess }) => async (id: string): Promise<boolean> => {
  try {
    await customFetch(`/metropolis/api/ed/event/participants/${id}`, {
      method: 'DELETE',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    addSuccess('Účastník byl vymazán');
    return true;
  } catch (err) {
    addError(err.message);
    return false;
  }
};

export const reserveEvent = ({ addSuccess, addError }) => handleLockedUser => async (eventId, myId, withSuccess) => {
  try {
    const response = await customFetch(`/metropolis/api/ed/events/lock/${eventId}/logged`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    const userId = response.headers.get('event-lock-user-id');
    const fullName = userId ? await getLockedFullName({ userId, addError }) : '';
    handleLockedUser({ id: userId, fullName });

    if (userId !== myId) {
      addError(`Událost je jíž rezervována uživatelem: ${fullName}`);
      return false;
    }

    if (response.ok && withSuccess) {
      addSuccess('Událost vám byla zarezervována');
    }
    return response.ok;
  } catch (e) {
    addError('Událost sa nepodařilo rezervovat');
    return false;
  }
};

export const releaseEvent = ({ addError }) => async eventId => {
  try {
    const response = await customFetch(`/metropolis/api/ed/events/lock/${eventId}/logged`, {
      method: 'DELETE',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    return response.ok;
  } catch (e) {
    addError('Událost sa nepodařilo uvolnit');
    return false;
  }
};
export const forceReleaseEvent = ({ addSuccess, addError }) => handleLockedUser => async eventId => {
  try {
    const response = await customFetch(`/metropolis/api/ed/events/lock/${eventId}`, {
      method: 'DELETE',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    if (response.ok) {
      addSuccess('Událost úspěšně uvolněna');
      handleLockedUser({});
    }
    return response.ok;
  } catch (e) {
    addError('Událost sa nepodařilo uvolnit');
    return false;
  }
};

export const convertToPdf = ({ addSuccess, addError }) => async fileId => {
  try {
    const response = await customFetch(`/metropolis/api/ed/record/geosps/convert-to-pdf?fileId=${fileId}`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    if (response.ok) {
      const text = await response.text();
      addSuccess('Příloha byla zkonvertována');
      return text;
    }
    addError('Nepodařilo se zkonvertovat přílohu');
    return null;
  } catch (e) {
    addError('Nepodařilo se zkonvertovat přílohu');
    return null;
  }
};

export const signPdf = ({ addSuccess, addError }) => async (
  fingerprint: string,
  content: string,
  signatureFormat: string,
  fileName: string
) => {
  try {
    const response = await fetch('http://localhost:31200/sign-document-base64', {
      method: 'POST',
      headers: new Headers({}),
      body: JSON.stringify({
        fileName,
        signatureFormat,
        fingerprint,
        content
      })
    });

    if (response.ok) {
      const text = await response.text();
      addSuccess('Příloha byla zkonvertována');
      return text;
    }

    if (response.status === 404) {
      addError('Nebyl nalezen signer');
    } else if (response.status === 400) {
      addError('Nebyl nalezen certifikát');
    } else {
      addError('Nepodařilo se podepsat přílohu');
    }
    return null;
  } catch (e) {
    addError('Nepodařilo se podepsat přílohu');
    return null;
  }
};

export const setProceedingNumber = ({ addSuccess, addError }) => async (documentId, fileId) => {
  try {
    const response = await customFetch(`/metropolis/api/ed/record/set-proceeding-number`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      params: { documentId, fileId }
    });

    if (response.ok) {
      const json = await response.json();
      addSuccess('Příloha byla přidána');
      return json;
    }
    addError('Nepodařilo se přidat přílohu');
    return null;
  } catch (e) {
    addError('Nepodařilo se přidat přílohu');
    return null;
  }
};

export const saveDocumentAttachment = ({ addSuccess, addError }) => async (documentId, body) => {
  try {
    const response = await customFetch(`/metropolis/api/ed/record/save-document-attachment`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      params: { documentId }
    });

    if (response.ok) {
      addSuccess('Příloha byla odeslána');
      const json = await response.json();
      return json;
    }
    addError('Nepodařilo se odeslat přílohu');
    return null;
  } catch (e) {
    addError('Nepodařilo se odeslat přílohu');
    return null;
  }
};

export const getAllStations = ({ addError }) => async (): Promise<string | null> => {
  try {
    const response = await customFetch(`/metropolis/api/ed/stations/all`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });
    const json = await response.json();
    return json;
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getEventByNumber = ({ addError }) => async (eventNumber: number) => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/events/parametrized`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        filter: [{ field: 'number', operation: 'EQ', value: eventNumber }]
      })
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    } else {
      addError('Událost nenalezena');
      return null;
    }
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getEventFiles = ({ addError }) => async (eventId: string) => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/event/files/${eventId}`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    } else {
      addError('Soubor nenalezen');
      return null;
    }
  } catch (err) {
    addError(err.message);
    return null;
  }
};

export const getFilters = async (module: string): Promise<{ filterSettings: string } | null> => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/${module}/settings/filter/`, {
      method: 'GET',
      headers: new Headers({ 'Content-Type': 'application/json' })
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    } else {
      return null;
    }
  } catch (err) {
    console.error('Error fetching filters:', err);
    return null;
  }
};

export const saveFilters = async (module: string, filter: Filter[]) => {
  try {
    const response: any = await customFetch(`/metropolis/api/ed/${module}/settings/filter/`, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ filter })
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    } else {
      return null;
    }
  } catch (err) {
    console.error('Error fetching filters:', err);
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/modules/events/_eventsActions.ts